import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatsCircle, Check, CheckCircle, FilmSlate } from '@phosphor-icons/react';
import BackButton from '../BackButton';
import { BTN_PRIMARY, BTN_SECONDARY } from '../buttonStyles';
import ButtonSpinner from '../ButtonSpinner';
import { busyStyle } from '../../utils/busyStyle';
import { CoachPhoto } from './CoachHeader';
import { getAvailableSlotsForCoach, getCoachSlotRange } from '../../services/coachAvailability';
import { useLiveConfig } from '../../hooks/useLiveConfig';
import { DEV_ANY_TIME_ID, dayTimes, requestDays } from '../../utils/lessonTimes';
import { liveLessonsErrorMessage } from '../../services/liveLessons';
import { remindLiveTimesOpen, subscribeCoachWeek, subscribeOpenReminder } from '../../services/liveV2';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { LIVE_CALL_MAX_DAYS_AHEAD } from '../../utils/bookingWindow';
import { CalendarPicker } from './CalendarPicker';
import { pickerDays } from '../../utils/calendarPicker';
import { CARD } from '../live/surfaces';
import { MODE_LABEL, modeOneLine, type LiveLessonMode } from '../../utils/liveLessonModes';
import { recordingLine } from '../../utils/liveLessonText';
import type { CoachAvailabilitySlot, LiveOfferingType } from '../../types/liveCall';
import { bookingDays, dayKey, firstDayPastWindow, usualSlot } from '../../utils/liveCalendar';

/**
 * The one booking screen: a member's Monthly Coaching lesson, and with
 * the header and button changed, Request a New Time, Suggest a Time and
 * Request a Time. Laid out as Skillest's booking page: the coach's photo
 * centred at the top with "Booking with Justin" and the lesson's line;
 * then Select a day (a month calendar, days with open times tinted, today
 * ringed, the rest greyed, the first open day chosen) beside Select a time
 * (the day's times in a column, "Your usual time" first); on a phone the
 * two stack. The time zone line sits under the calendar; its Change opens
 * the app's time zone sheet. Once a time is
 * picked, Match Review or Guidance Call where the mode is picked here, and
 * under Match Review whatever the caller asks about the match. The button
 * and the recording line close the page.
 *
 * Open times already loaded (the month page's) draw at once and are
 * refreshed behind; without them only the times wait, as a skeleton.
 */

const FOOTNOTE = 'text-[13px] leading-[18px] text-db-text-2';
const MODE_ICON = { match_review: FilmSlate, guidance_call: ChatsCircle } as const;

const ROW_TEXT = 'text-[17px] leading-[22px] tracking-[-0.43px] text-db-text-1';

/**
 * No open times in the window: the page is the header and this card. For a
 * player, Message {coach} and Remind Me (remindLiveTimesOpen, for the first
 * day past the window in the coach's zone, as the month page asks). For the
 * coach looking at their own times, the way to add some.
 */
function NoTimesCard({ coachId, coachFirstName, ownTimes, fallbackZone, onMessage }: {
  coachId: string;
  coachFirstName: string;
  ownTimes: boolean;
  fallbackZone: string;
  onMessage?: () => Promise<void>;
}) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showError } = useToast();
  const [messaging, setMessaging] = useState(false);
  const [coachZone, setCoachZone] = useState<string | null>(null);
  const [reminded, setReminded] = useState(false);
  const [reminding, setReminding] = useState(false);
  const [now] = useState(() => Date.now());
  useEffect(() => (ownTimes ? undefined : subscribeCoachWeek(coachId, (w) => setCoachZone(w?.timeZone ?? fallbackZone))),
    [ownTimes, coachId, fallbackZone]);
  const date = coachZone ? firstDayPastWindow(now, coachZone, LIVE_CALL_MAX_DAYS_AHEAD) : null;
  useEffect(() => {
    if (ownTimes || !currentUser || !date) return;
    return subscribeOpenReminder(coachId, date, currentUser.uid, setReminded);
  }, [ownTimes, currentUser, coachId, date]);

  const remind = async () => {
    if (!date) return;
    setReminding(true);
    try {
      await remindLiveTimesOpen({ coachId, date });
      setReminded(true);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not set the reminder. Please try again.'));
    } finally {
      setReminding(false);
    }
  };

  if (ownTimes) {
    return (
      <div className={`mx-auto mt-8 max-w-[560px] p-4 ${CARD}`} data-no-open-times>
        <p className={ROW_TEXT}>You have no open times in the next two weeks.</p>
        <button type="button" onClick={() => navigate('/account?tab=availability')} className={`${BTN_PRIMARY} mt-4 w-full`}>
          Open Availability
        </button>
      </div>
    );
  }
  return (
    <div className={`mx-auto mt-8 max-w-[560px] p-4 ${CARD}`} data-no-open-times>
      <p className={ROW_TEXT}>{coachFirstName} has no open times in the next two weeks.</p>
      {onMessage && (
        <button type="button" disabled={messaging} aria-busy={messaging} style={busyStyle(messaging)}
          onClick={() => { setMessaging(true); void onMessage().finally(() => setMessaging(false)); }}
          className={`${BTN_PRIMARY} mt-4 w-full`}>
          {messaging ? <ButtonSpinner /> : `Message ${coachFirstName}`}
        </button>
      )}
      <div className="mt-4 border-t border-[#e6e6e6] pt-4">
        <p className={ROW_TEXT}>We&apos;ll let you know when {coachFirstName} adds times.</p>
        {reminded ? (
          <p className="mt-3 flex items-center gap-2 text-[15px] leading-[20px] text-db-text-2" role="status">
            <CheckCircle size={18} weight="fill" className="flex-shrink-0 text-biscayne" aria-hidden="true" />
            We&apos;ll remind you.
          </p>
        ) : (
          <button type="button" onClick={() => { void remind(); }} disabled={!date || reminding} aria-busy={reminding}
            style={busyStyle(reminding)} className={`${BTN_SECONDARY} mt-3 w-full`}>
            {reminding ? <ButtonSpinner /> : 'Remind Me'}
          </button>
        )}
      </div>
    </div>
  );
}

export interface BookingScreenProps {
  coachId: string;
  offeringType: LiveOfferingType;
  coachName: string;
  coachFirstName: string;
  photoUrl?: string | null;
  /** "Booking with Justin", "Request a New Time", "Suggest a Time". */
  title: string;
  /** "Lesson 2 of 4 · 45 min, live on video". */
  line?: string;
  /** Under the header: a card problem, or "This replaces Tue, Oct 20 at 1:00 PM". */
  banner?: ReactNode;
  /** Open times already loaded elsewhere: drawn at once, refreshed behind. */
  initialSlots?: CoachAvailabilitySlot[] | null;
  /** The last start the server will book (bookingWindow.latestStartAt). */
  latestStartAt: string;
  timeZone: string;
  onZoneChange: (zone: string) => void;
  /** The member's weekly time: "Your usual time" leads the times when it is open. */
  weekly?: { weekday: number; time: string } | null;
  /** Match Review or Guidance Call, picked here once a time is. Left out where the mode is not this screen's to pick. */
  mode?: LiveLessonMode;
  onMode?: (mode: LiveLessonMode) => void;
  /** Under the Match Review choice: the caller's question about the match. */
  matchSection?: ReactNode;
  /** Under a chosen time, whatever the mode: Monthly Coaching's "Make it every Wednesday?". */
  afterTime?: ReactNode;
  selected: CoachAvailabilitySlot | null;
  onSelect: (slot: CoachAvailabilitySlot) => void;
  /** "Book Lesson", "Send to Justin". */
  buttonLabel: string;
  busy?: boolean;
  /** Why this cannot be booked: shown over the button, which waits. */
  blocked?: string | null;
  /** The button waits for something shown elsewhere (a card problem in the banner). */
  locked?: boolean;
  onSubmit: () => void;
  onBack: () => void;
  /** With no open times in the window: Message {coach}, opening their thread. */
  onMessage?: () => Promise<void>;
  /** The coach is looking at their own open times (Suggest a Time, the coach's Request a New Time). */
  ownTimes?: boolean;
  /**
   * DEV ONLY: LIVE_DEV_ANY_TIME. This screen may draw any time when config/live says so: every day from today
   * and the whole 6:00 AM to 11:00 PM grid, the past and the coach's other lessons greyed. A drawn time's id
   * starts DEV_ANY_TIME_ID, so the caller sends its start (devAnyTimeStart). Off the switch it changes nothing.
   */
  devAnyTime?: boolean;
  /** Inside a Sheet (components.md 10): its bar carries the title and Cancel, so no back circle and no second title. */
  inSheet?: boolean;
}

export function BookingScreen({
  coachId, offeringType, coachName, coachFirstName, photoUrl, title, line, banner, initialSlots,
  latestStartAt, timeZone, onZoneChange, weekly, mode, onMode, matchSection, afterTime, selected, onSelect,
  buttonLabel, busy = false, blocked, locked = false, onSubmit, onBack, onMessage, ownTimes = false, devAnyTime = false, inSheet = false,
}: BookingScreenProps) {
  const { userProfile } = useAuth();
  const seeded = initialSlots?.filter((s) => !s.offeringTypes || s.offeringTypes.includes(offeringType)) ?? null;
  const [slots, setSlots] = useState<CoachAvailabilitySlot[]>(seeded ?? []);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(seeded ? 'ready' : 'loading');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    getAvailableSlotsForCoach(coachId, undefined, offeringType)
      .then((s) => { if (live) { setSlots(s); setState('ready'); } })
      // Times already on screen stay; only a first load shows the error.
      .catch(() => { if (live) setState((cur) => (cur === 'ready' ? cur : 'error')); });
    return () => { live = false; };
  }, [coachId, offeringType, attempt]);

  const [now] = useState(() => new Date().toISOString());
  // DEV ONLY: LIVE_DEV_ANY_TIME. With the switch on (config/live), any time from today, not the open times.
  const config = useLiveConfig();
  const anyTime = devAnyTime && config?.devAnyTime === true;
  const [booked, setBooked] = useState<Array<{ startAt: string; minutes: number }>>([]);
  useEffect(() => {
    if (!anyTime) return;
    let live = true;
    // DEV ONLY: LIVE_DEV_ANY_TIME. The coach's other lessons, greyed; unread, the server still refuses a clash.
    getCoachSlotRange(coachId, now, new Date(Date.parse(now) + 15 * 86_400_000).toISOString())
      .then((s) => { if (live) setBooked(s.filter((x) => x.isBooked).map((x) => ({ startAt: x.startAt, minutes: x.durationMinutes }))); })
      .catch(() => {});
    return () => { live = false; };
  }, [anyTime, coachId, now]);
  const days = useMemo(() => (anyTime ? requestDays(Date.parse(now), now, timeZone, true) : bookingDays(now, latestStartAt, timeZone)),
    [anyTime, now, latestStartAt, timeZone]);
  // DEV ONLY: LIVE_DEV_ANY_TIME. Every time on config/live's grid, 6:00 AM to 11:00 PM; the refused ones are kept, greyed.
  const drawn = useMemo(() => {
    if (!anyTime || !config) return null;
    const greyed = new Set<string>();
    const all = days.flatMap((day) => dayTimes({ day, tz: timeZone, step: config.slotMinutes, nowMs: Date.parse(now),
      leadMinutes: 0, original: now, current: '', minutes: 45, busy: booked, anyTime: true }).map((t) => {
      const id = `${DEV_ANY_TIME_ID}${t.startAt}`;
      if (t.state !== 'open') greyed.add(id);
      return { id, coachId, startAt: t.startAt, endAt: new Date(Date.parse(t.startAt) + 45 * 60_000).toISOString(),
        durationMinutes: 45, isBooked: false, createdAt: now } satisfies CoachAvailabilitySlot;
    }));
    return { all, greyed };
  }, [anyTime, config, days, timeZone, now, booked, coachId]);
  const inWindow = useMemo(() => {
    if (drawn) return drawn.all;
    const keys = new Set(days);
    return slots.filter((s) => keys.has(dayKey(s.startAt, timeZone)));
  }, [drawn, slots, days, timeZone]);
  const known = state === 'ready' || !!drawn;
  // DEV ONLY: LIVE_DEV_ANY_TIME. A day with drawn times has times when one of them can still be picked.
  const { hasTimes } = pickerDays(days, inWindow, timeZone, Date.parse(now), drawn?.greyed);
  const usual = useMemo(() => usualSlot(drawn ? inWindow.filter((s) => !drawn.greyed.has(s.id)) : inWindow, weekly, timeZone),
    [drawn, inWindow, weekly, timeZone]);
  const anyTimes = days.some(hasTimes);
  // Known to have none in the window: the header and one card, nothing to pick.
  const none = known && !anyTimes;

  return (
    <div className={inSheet ? '' : 'min-h-screen bg-db-bg'}>
      <div className={inSheet ? 'w-full pb-4 pt-2' : 'mx-auto w-full max-w-[880px] px-4 pb-10 pt-3.5'}>
        {!inSheet && <div className="mb-2"><BackButton onClick={onBack} flush gutter /></div>}

        {/* The coach, centred */}
        <div className="flex flex-col items-center text-center">
          <CoachPhoto url={photoUrl} name={coachName} size={72} />
          {!inSheet && <h1 className="mt-3 text-[22px] leading-[28px] font-bold text-db-text-1">{title}</h1>}
          {/* DEV ONLY: LIVE_DEV_ANY_TIME. So nobody mistakes it for production. */}
          {drawn && <DevAnyTimeTag />}
          {line && <p className="mt-1 max-w-[480px] text-[15px] leading-[20px] text-db-text-2 [text-wrap:balance]">{line}</p>}
        </div>
        {none ? (
          <NoTimesCard coachId={coachId} coachFirstName={coachFirstName} ownTimes={ownTimes} fallbackZone={timeZone}
            onMessage={onMessage} />
        ) : (
          <>
          {banner && <div className="mx-auto mt-6 max-w-[560px]">{banner}</div>}

          {/* The calendar picker (components.md 6): the month, the zone line, the day's times beneath. */}
          <div className="mt-8">
            <CalendarPicker days={days} times={inWindow} timeZone={timeZone} onZoneChange={onZoneChange}
              state={drawn ? 'ready' : state} onRetry={() => { setState('loading'); setAttempt((n) => n + 1); }}
              selectedIds={selected ? [selected.id] : []} onPick={onSelect} usual={usual} disabledIds={drawn?.greyed} />
          </div>

          <div className="mx-auto max-w-[560px]">
            {/* Match Review or Guidance Call, once a time is picked */}
            {mode && onMode && selected && (
              <div className="mt-8 flex flex-col gap-3" role="radiogroup" aria-label="Kind of lesson">
                {(['match_review', 'guidance_call'] as const).map((key) => {
                  const on = mode === key;
                  const Icon = MODE_ICON[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => onMode(key)}
                      className={
                        `ios-press flex w-full cursor-pointer items-center gap-3.5 p-4 text-left ${CARD} ` +
                        'focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 ' +
                        (on ? 'ring-2 ring-inset ring-biscayne' : '')
                      }
                    >
                      <Icon size={24} weight="regular" className="flex-shrink-0 text-biscayne" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">{MODE_LABEL[key]}</span>
                        <span className="block text-[15px] leading-[20px] text-db-text-2">{modeOneLine(key, coachFirstName)}</span>
                      </span>
                      <span aria-hidden="true" className={
                        'flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full ' +
                        (on ? 'bg-biscayne text-white' : 'border-[1.5px] border-[rgba(60,60,67,0.3)]')
                      }>
                        {on && <Check size={13} weight="bold" />}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            {mode === 'match_review' && selected && matchSection}
            {selected && afterTime}

            {blocked && <p className="mt-6 text-[15px] leading-[20px] text-db-text-1" role="status">{blocked}</p>}
            <button type="button" onClick={onSubmit} disabled={!selected || busy || !!blocked || locked} aria-busy={busy}
              style={busyStyle(busy)} className={`${BTN_PRIMARY} mt-8 w-full`}>
              {busy ? <ButtonSpinner /> : buttonLabel}
            </button>
            {/* The player's footnote; no coach screen carries it (Ryan, 29 Oct). */}
            {/* Not in a sheet (169; the CTA audit of 5 Nov, row 37): not needed to pick a time; the call says it. */}
            {!ownTimes && !inSheet && <p className={`mt-3 text-center ${FOOTNOTE}`}>{recordingLine(userProfile?.playerProfile?.age)}</p>}
          </div>
          </>
        )}
      </div>
    </div>
  );
}

/** DEV ONLY: LIVE_DEV_ANY_TIME. The small grey tag under a sheet's title while any time is open. */
export function DevAnyTimeTag() {
  return (
    <span className="mt-1 inline-block rounded-[100px] bg-[rgba(120,120,128,0.12)] px-2 text-[11px] leading-[16px] font-semibold text-db-text-2"
      data-dev-any-time>
      Dev: any time
    </span>
  );
}
